import {
  Color,
  ImageScaleView,
  MultiplyFilter,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import { magicLampsAnim, magicLampsAnimEx } from './MagicLampsAnim';
import {
  MagicLampsPrize,
  MagicLampsPrizeId,
} from 'src/replicant/match2/magiclamps/magiclamps.ruleset';
import { MagicLampsGenie } from './MagicLampsGenie';
import { MagicLampsRewardIcon } from './MagicLampsRewardIcon';
import { MagicLampsSmoke } from './MagicLampsSmoke';
import { ShineStarsFx } from '../components/ShineStarsFX';
import { magicLampsPlaySfx } from './magiclamps.utils';

interface MagicLampsLampOpts extends ViewOpts {
  onSelect?: () => void;
}

export class MagicLampsLamp extends View {
  private container: View;
  private lamp: ImageScaleView;
  private top: ImageScaleView;
  private filter: MultiplyFilter;
  private filterColor: Color;
  private _brightness = 1;
  private topX = 0;
  private topY = 0;
  private smoke: MagicLampsSmoke;
  private shine: ShineStarsFx;
  public prizeId?: MagicLampsPrizeId;
  public prize?: View;
  public trap = false;
  public out = false;
  public opened = false;

  constructor(opts: MagicLampsLampOpts) {
    super({ ...opts });

    this.filterColor = new Color('#FFFFFF');
    this.filter = new MultiplyFilter(this.filterColor);

    this.lamp = new ImageScaleView({
      superview: this,
      width: 122,
      height: 96,
      y: -55,
      image: `assets/ui/match2/magiclamps/magiclamps_lamp_01.png`,
      centerAnchor: true,
      centerOnOrigin: true,
      canHandleEvents: true,
      filter: this.filter,
    });

    this.lamp.onInputStart = () => opts.onSelect?.();

    this.top = new ImageScaleView({
      superview: this,
      width: 33,
      height: 49,
      y: this.lamp.style.y - 66,
      image: `assets/ui/match2/magiclamps/magiclamps_lamp_top_01.png`,
      centerAnchor: true,
      centerOnOrigin: true,
      filter: this.filter,
    });
    this.topX = this.top.style.x;
    this.topY = this.top.style.y;

    this.container = new View({
      superview: this,
      x: this.topX,
      y: this.topY,
      visible: false,
    });

    this.smoke = new MagicLampsSmoke({
      superview: this.container,
      x: 0,
      y: -10,
      zIndex: 1,
    });

    this.shine = new ShineStarsFx({
      superview: this.container,
      width: 90,
      height: 90,
      centerOnOrigin: true,
      zIndex: 1,
    });
  }

  /**
   * Set current stage for this lamp, changing the lamp and top images.
   */
  public setStage(stage: number) {
    const type = String(stage).padStart(2, '0');
    this.lamp.updateOpts({
      image: `assets/ui/match2/magiclamps/magiclamps_lamp_${type}.png`,
    });
    this.top.updateOpts({
      image: `assets/ui/match2/magiclamps/magiclamps_lamp_top_${type}.png`,
    });
  }

  /**
   * Set the prize for this lamp. Could be a reward or a trap.
   */
  public setPrize(prize?: MagicLampsPrize) {
    this.trap = false;
    this.prizeId = prize?.type;

    let prizeView;

    if (prize) {
      if (prize.type === 'trap') {
        this.trap = true;
        prizeView = new MagicLampsGenie({ scale: 0.7, y: -10 });
      } else {
        prizeView = new MagicLampsRewardIcon({ x: -50, y: -60 });
        prizeView.setup({ type: prize.type, value: prize.value, scale: 0.7 });
      }
    }

    this.prize?.removeFromSuperview();
    this.prize = prizeView;
    this.prize?.updateOpts({
      superview: this.container,
    });
  }

  /**
   * Make the lamp do a little jump to the front.
   */
  public async bringToFront() {
    magicLampsPlaySfx('button-power');
    this.brightnessNormal();
    await magicLampsAnim(
      this,
      { y: -60, scaleX: 1, scaleY: 1.2 },
      150,
      animate.easeOutSine,
    );
    await magicLampsAnim(
      this,
      { y: 5, scaleX: 1.1, scaleY: 0.9 },
      150,
      animate.easeInSine,
    );
    magicLampsPlaySfx('button-basic');
    await magicLampsAnim(
      this,
      { scaleX: 1, scaleY: 1 },
      300,
      animate.easeOutBack,
    );
  }

  /**
   * Send the lamp to the background
   */
  public async sendToBackground() {
    this.brightnessLow();
    await magicLampsAnim(
      this,
      { y: 0, scaleX: 1, scaleY: 1 },
      200,
      animate.easeOutSine,
    );
  }

  /**
   * Bring the lamp back to its normal state
   */
  public async backToIdle() {
    this.brightnessNormal();
    await magicLampsAnim(
      this,
      { y: 0, scaleX: 1, scaleY: 1 },
      200,
      animate.easeOutSine,
    );
  }

  /**
   * Open the lamp, revealing its contents.
   */
  public async open() {
    magicLampsAnim(this.top, { y: this.topY - 150, opacity: 0 }, 300);

    const prizeY = this.topY - 50;
    this.container.updateOpts({ visible: true, scale: 0 });

    this.smoke.playAnimation();
    await magicLampsAnim(this.container, { y: prizeY, scale: 1 }, 300);

    if (!this.trap && this.brightness === 1) this.shine.play();

    this.opened = true;
    this.floatLoop();
  }

  /**
   * Close the lamp, hiding its contents.
   */
  public async close() {
    this.opened = false;
    this.shine.stop();
    magicLampsAnim(this.top, { y: this.topY, opacity: 1 }, 300);
    await magicLampsAnim(this.container, { y: this.topY, scale: 0 }, 300);
    this.container.updateOpts({ visible: false });
  }

  /**
   * Reset the lamp back to normal
   */
  public async reset() {
    await this.close();
    await this.backToIdle();
  }

  /**
   * Hide the lamp, making it jump out of the stage
   */
  public async jumpOut() {
    this.out = true;
    magicLampsPlaySfx('puzzle-invalid'); // Sounds like movement
    magicLampsAnimEx(this, { brightness: 0.9 }, 200);
    await magicLampsAnim(
      this,
      { y: -100, scaleX: 0.9, scaleY: 0.9 },
      200,
      animate.easeOutSine,
    );
    magicLampsAnimEx(this, { brightness: 0.8 }, 200);
    await magicLampsAnim(
      this,
      { y: 10, scaleX: 0.5, scaleY: 0.8, opacity: 0 },
      200,
      animate.easeInSine,
    );
  }

  /**
   * Show the lamp, making it jump back to the stage
   */
  public async jumpIn() {
    this.out = false;
    magicLampsPlaySfx('puzzle-invalid'); // Sounds like movement
    this.updateOpts({ opacity: 0, scaleX: 0.5, scaleY: 0.8, y: 10 });
    this.brightness = 0.8;
    magicLampsAnimEx(this, { brightness: 0.9 }, 200);
    await magicLampsAnim(
      this,
      { y: -100, scaleX: 0.9, scaleY: 0.9, opacity: 1 },
      200,
      animate.easeOutSine,
    );
    magicLampsAnimEx(this, { brightness: 1 }, 200);
    await magicLampsAnim(
      this,
      { y: 0, scaleX: 1, scaleY: 1.2 },
      200,
      animate.easeInSine,
    );
    await magicLampsAnim(
      this,
      { scaleX: 1.1, scaleY: 0.9 },
      100,
      animate.easeOutSine,
    );
    await magicLampsAnim(
      this,
      { scaleX: 1, scaleY: 1 },
      300,
      animate.easeOutBack,
    );
  }

  /**
   * Loop the icon floating animation - will be interrupted when the lamp is closed.
   */
  private async floatLoop(i = 1) {
    if (!this.opened) return;
    const prizeY = this.topY - 50;
    await magicLampsAnim(
      this.container,
      { y: prizeY + 10 * (i % 2) },
      300,
      animate.easeInOutSine,
    );
    this.floatLoop(i + 1);
  }

  /**
   * Set the lamp and its contents to normal brightness
   */
  public async brightnessNormal() {
    const duration = 200;
    magicLampsAnimEx(this.container.style, { opacity: 1 }, duration);
    await magicLampsAnimEx(this, { brightness: 1 }, duration);
  }

  /**
   * Darken the lamp and its contents
   */
  public async brightnessLow() {
    const duration = 200;
    magicLampsAnimEx(this.container.style, { opacity: 0.5 }, duration);
    await magicLampsAnimEx(this, { brightness: 0.7 }, duration);
  }

  public get brightness() {
    return this._brightness;
  }

  public set brightness(v: number) {
    this._brightness = v;
    this.filterColor.r = 255 * v;
    this.filterColor.g = 255 * v;
    this.filterColor.b = 255 * v;
    this.filter.update(this.filterColor);
  }
}
